import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  SelectProps,
  Tooltip,
  styled,
} from '@mui/material';
import { DefaultComponentProps, OverridableTypeMap } from '@mui/material/OverridableComponent';
import { Control, Controller, FieldValues, Path, PathValue } from 'react-hook-form';

type IProps<ControlData extends FieldValues = FieldValues> = {
  id: Path<ControlData>;
  label?: string;
  control: Control<ControlData>;
  defaultValue?: PathValue<ControlData, Path<ControlData>>;
  children: React.ReactNode;
  selectProps?: SelectProps;
  toolTipTitle?: string;
  onChange?: (value: string) => void;
} & DefaultComponentProps<OverridableTypeMap>;

const StyledFormHelperText = styled(FormHelperText)({
  position: 'absolute',
  bottom: -20,
  marginTop: 0,
  marginLeft: 0,
});

const FormSelect = <T extends FieldValues = FieldValues>(props: IProps<T>) => {
  const {
    id,
    label,
    control,
    defaultValue,
    error,
    children,
    multiple,
    selectProps,
    toolTipTitle = '',
    onChange,
    ...rest
  } = props;
  return (
    <FormControl {...rest} error={!!error}>
      <InputLabel id={`${id}-label`}>{label}</InputLabel>
      <Controller
        render={({ field }) =>
          toolTipTitle ? (
            <Tooltip title={toolTipTitle}>
              <Select
                multiple={multiple}
                labelId={`${id}-label`}
                id={id}
                label={label}
                onChange={(e) => {
                  const v = e.target.value as string[];
                  if (v instanceof Object && 'includes' in v && v.includes('all')) return;
                  field.onChange(e);
                  onChange && onChange(e.target.value as string);
                }}
                value={field.value}
                {...selectProps}
              >
                {children}
              </Select>
            </Tooltip>
          ) : (
            <Select
              multiple={multiple}
              labelId={`${id}-label`}
              id={id}
              label={label}
              onChange={(e) => {
                const v = e.target.value as string[];
                if (v instanceof Object && 'includes' in v && v.includes('all')) return;
                field.onChange(e);
                onChange && onChange(e.target.value as string);
              }}
              value={field.value}
              {...selectProps}
            >
              {children}
            </Select>
          )
        }
        name={id}
        control={control}
        defaultValue={defaultValue}
        {...rest}
      />
      {!!error && <StyledFormHelperText>{error}</StyledFormHelperText>}
    </FormControl>
  );
};

export default FormSelect;
